import React, { FC, memo, useMemo, useState } from 'react';
import {
  TableCell,
  TextField,
  CircularProgress,
  Typography,
  Paper,
  Box,
  Button
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import useQuotationProduct from 'src/hooks/quotation-v2/use-quotation-products';
import { Quotation, QuotationCategory, QuotationProduct } from 'src/types';
import { ProductsOption } from './ProductsOption';
import { isEmpty } from 'lodash';
import { colors } from 'src/constants';

interface Props {
  index: number;
  quotation: Quotation;
  category?: QuotationCategory;
  isScreenCapture: boolean;
}

const Component: FC<Props> = ({
  index,
  quotation,
  category,
  isScreenCapture = false
}) => {
  const {
    is_loading_products,
    quotationData,
    productOptions,
    onChangeProduct,
    onChangeRemoveVoucherRow
  } = useQuotationProduct();

  const isCustom = Boolean(quotation?.custom);
  const [isSortedByPriceAsc, setIsSortedByPriceAsc] = useState<Boolean>(true);

  const filteredOptionsByCategory = useMemo(() => {
    const filtered = productOptions?.filter(
      (products) => products?.category_id === quotation?.product?.category_id
    );
    return [...filtered].sort((a, b) =>
      isSortedByPriceAsc
        ? (a?.retail_price || 0) - (b?.retail_price || 0)
        : (b?.retail_price || 0) - (a?.retail_price || 0)
    );
  }, [productOptions, quotation.product.category_id, isSortedByPriceAsc]);

  const onHandleChangeValue = (
    index: number,
    newValue: QuotationProduct | string | null,
    category: QuotationCategory | undefined,
    quotationData: Quotation[]
  ) => {
    // Handle clear
    if (!newValue) {
      onChangeProduct(index, category || {}, {}, quotationData);
      return;
    }

    // Handle freeSolo text input (custom)
    if (typeof newValue === 'string') {
      const customProduct: QuotationProduct = {
        product_name: newValue,
        retail_price: quotation.product?.retail_price || 0,
        category_id: category?.category_id || 0,
        category_name: category?.category_name || ''
      };

      onChangeProduct(index, category || {}, customProduct, quotationData);
      return;
    }

    // Handle normal object selection
    if (!isEmpty(newValue)) {
      onChangeProduct(index, category || {}, newValue, quotationData);
    }
  };

  return (
    <TableCell style={{ padding: 0, width: '475px', paddingRight: '15px' }}>
      {isScreenCapture ? (
        <div>
          <Typography
            variant={'h6'}
            style={{ fontWeight: 700, fontSize: '18px' }}
          >
            {quotation.product?.product_name}
          </Typography>
        </div>
      ) : (
        <Autocomplete
          freeSolo={isCustom}
          disabled={quotation.product.category_name === 'VOUCHER'}
          value={
            isCustom
              ? quotation.product?.product_name || ''
              : quotation.product || null
          }
          onChange={(_, newValue) => {
            if (!newValue && quotationData[index].product.product_name) {
              onChangeRemoveVoucherRow(quotationData);
            } else {
              onHandleChangeValue(index, newValue, category, quotationData);
            }
          }}
          onBlur={(e: any) => {
            if (isCustom && e.target.value) {
              onHandleChangeValue(
                index,
                e.target.value,
                category,
                quotationData
              );
            }
          }}
          options={filteredOptionsByCategory}
          getOptionLabel={(option) =>
            typeof option === 'string' ? option : option?.product_name || ''
          }
          loading={is_loading_products}
          size="small"
          fullWidth
          renderOption={(option) =>
            typeof option === 'string' ? (
              option
            ) : (
              <ProductsOption product={option} showSRP={false} />
            )
          }
          renderInput={(params) => (
            <TextField
              {...params}
              style={{ width: '460px' }}
              multiline
              InputProps={{
                ...params.InputProps,
                disableUnderline: true,
                style: {
                  whiteSpace: 'normal',
                  overflowWrap: 'break-word',
                  color: isCustom ? '#1e88e5' : 'inherit',
                  fontWeight: 700,
                  minWidth: '520px',
                  marginLeft: '10px'
                },
                endAdornment: (
                  <>
                    {is_loading_products && (
                      <CircularProgress color="inherit" size={20} />
                    )}
                  </>
                )
              }}
            />
          )}
          PaperComponent={({ children }) => (
            <Paper>
              <Box style={{ display: 'flex', flexDirection: 'column' }}>
                <Button
                  style={{
                    background: isSortedByPriceAsc ? colors?.primary : '',
                    color: isSortedByPriceAsc ? 'white' : colors?.primary,
                    border: `1px solid ${
                      isSortedByPriceAsc ? 'white' : colors?.primary
                    }`,
                    padding: 5,
                    margin: 20,
                    marginBottom: 0,
                    width: '55%',
                    alignSelf: 'flex-end'
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsSortedByPriceAsc(!isSortedByPriceAsc);
                  }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                  }}
                >
                  {isSortedByPriceAsc
                    ? 'Sort By Price (Lowest to Highest)'
                    : 'Sort By Price (Highest to Lowest)'}
                </Button>
                {children}
              </Box>
            </Paper>
          )}
        />
      )}
    </TableCell>
  );
};

export const QuotationProductNameCell = memo(Component);
